import { ClbIcon } from "@celebration/react";
import { useMemo } from "react";
import styles from "./RegionalProgress.module.scss";

export interface MergedSubStep {
  stepId: string;
  name: string;
  eventType: string;
  isProcessed: boolean;
}

export interface RegionalProgressProps {
  steps: MergedSubStep[];
  parentIsNext?: boolean;
}

const getStatusIcon = (eventType: string, isProcessed: boolean, isNext: boolean) => {
  // Se foi processado, mostra o ícone do status
  if (isProcessed) {
    switch (eventType?.toUpperCase()) {
      case "ERROR":
        return <ClbIcon icon="cancel" size="sm" style={{ color: "#D32F2F" }} />;
      case "IN_PROGRESS":
      case "PROCESSING":
        return <ClbIcon icon="loader" size="sm" style={{ color: "#2B83F7" }} />;
      case "SUCCESS":
      case "FINISH":
      default:
        return <ClbIcon icon="checkbox-circle" size="sm" style={{ color: "#2E7D32" }} />;
    }
  }

  // Se é a próxima etapa, mostra loading azul
  if (isNext) {
    return <ClbIcon icon="loader" size="sm" style={{ color: "#2B83F7" }} />;
  }

  // Etapas futuras: sem ícone (apenas espaço reservado)
  return <span style={{ width: 20, height: 20 }} />;
};

const RegionalProgress: React.FC<RegionalProgressProps> = ({ steps, parentIsNext = false }) => {
  // Identifica o índice do próximo regional a ser processado
  // Hook deve ser chamado antes de qualquer early return
  const nextRegionalIndex = useMemo(() => {
    if (!steps?.length) return -1;
    return steps.findIndex((step) => !step.isProcessed);
  }, [steps]);

  if (!steps?.length) return null;

  return (
    <div className={styles["regional-progress"]} data-testid="regional-progress">
      {steps.map((step, index) => {
        const itemClasses = [
          styles["regional-progress__item"],
          !step.isProcessed && styles["regional-progress__item--disabled"],
        ].filter(Boolean).join(" ");

        // Só mostra loading no próximo regional se o parent (Etapa 07) for a próxima etapa
        const isNextRegional = parentIsNext && index === nextRegionalIndex;

        return (
          <div key={step.stepId} className={itemClasses}>
            {getStatusIcon(step.eventType, step.isProcessed, isNextRegional)}
            <span className={styles["regional-progress__item-name"]}>
              {step.name}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default RegionalProgress;
